/**
 * Notice when the running bundle is no longer the deployed one, and reload.
 *
 * ## Why a client-side check exists at all
 *
 * `index.html` is the only file naming the current content-hashed chunks, so a browser holding a
 * stale copy runs a stale application indefinitely. The server sends `Cache-Control: no-cache` on
 * it, which fixes every copy fetched from that point on — but it cannot reach a copy already
 * stored under the old rules, and a browser holding both the old HTML and its old chunks makes no
 * request at all. There is no request to answer. The user sees a version that was replaced weeks
 * ago, the deploy reports success, and nothing anywhere is wrong except what is on the screen.
 *
 * That happened three times before this existed. This is the part that can only be fixed from
 * inside the page.
 *
 * ## How
 *
 * Fetch `index.html` with `cache: 'no-store'` and compare the entry chunk it names with the one
 * actually running. Different means a deploy has happened underneath us.
 *
 * `no-store` rather than `no-cache`: this request must reach the network, and it is the one place
 * in the application where a conditional request is not good enough — a 304 would be answered from
 * the very cache entry we are trying to see past.
 *
 * ## When
 *
 * On returning to the tab, and hourly. Not on a tight timer: a dashboard left open on a wall
 * display should not poll all night, and the moment that actually matters is when somebody comes
 * back to it. Never during the first ten seconds, so a genuine cold start is not mistaken for
 * staleness.
 */

/** The entry chunk this page was booted with, read from the document that loaded it. */
const runningEntry = (): string | null => {
  for (const script of Array.from(document.querySelectorAll('script[src]'))) {
    const src = script.getAttribute('src') ?? ''
    const match = /\/assets\/(index-[A-Za-z0-9_-]+\.js)/.exec(src)

    if (match) return match[1]
  }

  return null
}

const deployedEntry = async (): Promise<string | null> => {
  const response = await fetch(`/index.html?_=${Date.now()}`, {
    cache: 'no-store',
    headers: { Accept: 'text/html' },
  })

  if (!response.ok) return null

  const match = /\/assets\/(index-[A-Za-z0-9_-]+\.js)/.exec(await response.text())

  return match ? match[1] : null
}

export interface FreshnessOptions {
  /** Overridable so a test does not have to wait an hour. */
  intervalMs?: number
  /** Injected so a test can assert the decision without navigating. */
  reload?: () => void
}

export const watchForNewDeploy = (options: FreshnessOptions = {}): (() => void) => {
  const intervalMs = options.intervalMs ?? 3_600_000
  const reload = options.reload ?? (() => window.location.reload())
  const running = runningEntry()

  // No entry chunk in the document means dev mode, or a layout this cannot read. Doing nothing is
  // the only safe answer: a reload loop is far worse than a stale tab.
  if (running === null) return () => {}

  let stopped = false

  const check = async (): Promise<void> => {
    if (stopped || document.visibilityState !== 'visible') return

    try {
      const deployed = await deployedEntry()

      if (deployed !== null && deployed !== running) reload()
    } catch {
      // Offline, or the server is briefly unavailable mid-deploy. Neither is evidence of
      // staleness, and reloading into a deploy in progress is how you get a blank page.
    }
  }

  const timer = setInterval(() => void check(), intervalMs)
  const onVisible = (): void => void check()

  document.addEventListener('visibilitychange', onVisible)

  // A grace period, so a cold start is never read as stale.
  const initial = setTimeout(() => void check(), 10_000)

  return () => {
    stopped = true
    clearInterval(timer)
    clearTimeout(initial)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
